/**
 * Outlook parent tools — schemas and Office.js (mailbox) handler implementations.
 *
 * Unlike the document hosts (Excel/Word/PowerPoint) there is NO `.run(context)`
 * batching model here: Outlook is a MAIL host. The current message lives at
 * `Office.context.mailbox.item` and its members are exposed through the older
 * callback-style async API (`getAsync`/`setAsync`). We promisify that once
 * (`callOffice`) and build every tool on top of it.
 *
 * The same task pane runs in TWO modes against the same `item`:
 *   - READ mode    — viewing a received message; header fields (`subject`, `from`,
 *                    `to`, …) are plain *values*, the body is read via `getAsync`.
 *   - COMPOSE mode — drafting a message; the same fields are *objects* with
 *                    async getters/setters, and the body/recipients are writable.
 * We detect the mode from the shape of `item.to` (a Recipients object exposes
 * `addAsync`; a read-mode array does not) and gate the write tools accordingly.
 *
 * Tool logic is intentionally mode-aware rather than two separate tool sets, so
 * the AI sees one coherent surface and gets a clear error if it tries to write
 * while only reading.
 *
 * Reply-safe writes (the crux): a reply/forward draft already contains the
 * quoted original thread. `set_body` REPLACES the whole body and would clobber
 * that quote, so it is NOT the way to answer a reply. The reply-safe surface is,
 * in order of preference:
 *   - read mode  → `reply` / `reply_all` open a reply form with our text on top
 *                  of the quote (Outlook preserves the thread for us);
 *   - compose mode (a reply draft is already open) → `prepend_to_body` /
 *                  `insert_at_cursor` add our text without touching the quote.
 * `set_body` stays for the "write a fresh message from scratch" case and says so.
 *
 * Requirement set: the manifest declares Mailbox 1.8. Members above the base set
 * (`categories`, `getAllInternetHeadersAsync` → 1.8) are still feature-detected
 * at call time so a client that under-reports its surface fails with a clear
 * message instead of a `TypeError`.
 */

import { ParentToolSchema, ToolCallCapabilities } from "@usable/office-embed";
import { graphTools } from "./graph-tools";

// ---------------------------------------------------------------------------
// Minimal typed surface for the mailbox item we touch.
//
// `@types/office-js` types `Office.context.mailbox.item` as a broad union of
// read/compose/appointment shapes, which makes direct member access awkward
// under `strict`. We cast once to this narrow interface describing exactly the
// members we use — it also documents the read-vs-compose split and keeps the
// test mocks honest (they implement this same shape).
// ---------------------------------------------------------------------------

type OfficeStatus = Office.AsyncResultStatus;
interface OfficeAsyncResult<T> {
  status: OfficeStatus;
  value: T;
  error?: { message?: string; name?: string; code?: number };
}
type AsyncCallback<T> = (result: OfficeAsyncResult<T>) => void;

/** A recipient as read (read mode) or returned by a compose getAsync. */
interface RecipientDetail {
  displayName?: string;
  emailAddress?: string;
}

/** Compose-mode recipient field (To / Cc / Bcc). */
interface ComposeRecipients {
  getAsync(cb: AsyncCallback<RecipientDetail[]>): void;
  setAsync(recipients: unknown, options: unknown, cb: AsyncCallback<void>): void;
  addAsync(recipients: unknown, options: unknown, cb: AsyncCallback<void>): void;
}

/** Compose-mode subject field. */
interface ComposeSubject {
  getAsync(cb: AsyncCallback<string>): void;
  setAsync(subject: string, options: unknown, cb: AsyncCallback<void>): void;
}

/** Message body — `getAsync` exists in both modes; the writers are compose-only. */
interface MessageBody {
  getAsync(coercionType: Office.CoercionType, cb: AsyncCallback<string>): void;
  setAsync(data: string, options: unknown, cb: AsyncCallback<void>): void;
  setSelectedDataAsync(data: string, options: unknown, cb: AsyncCallback<void>): void;
  // Mailbox 1.1 — adds to the BEGINNING of the body without replacing it; the
  // reply-safe way to put our text above a quoted thread in an open draft.
  prependAsync(data: string, options: unknown, cb: AsyncCallback<void>): void;
}

interface AttachmentDetail {
  id?: string;
  name?: string;
  contentType?: string;
  size?: number;
  isInline?: boolean;
  attachmentType?: string;
}

/** Item categories (Mailbox 1.8). The colour comes from the mailbox master list. */
interface CategoryDetail {
  displayName?: string;
  color?: string;
}
interface ItemCategories {
  getAsync(cb: AsyncCallback<CategoryDetail[]>): void;
  addAsync(categories: string[], cb: AsyncCallback<void>): void;
  removeAsync(categories: string[], cb: AsyncCallback<void>): void;
}

/** Form data for the read-mode reply surfaces (Mailbox 1.1). */
interface ReplyFormData {
  htmlBody?: string;
}

/** Parameters for `mailbox.displayNewMessageForm` (Mailbox 1.6). */
interface NewMessageForm {
  toRecipients?: string[];
  ccRecipients?: string[];
  bccRecipients?: string[];
  subject?: string;
  htmlBody?: string;
}

/** Parameters for `mailbox.displayNewAppointmentForm` (Mailbox 1.1). */
interface AppointmentForm {
  subject?: string;
  start?: Date;
  end?: Date;
  location?: string;
  body?: string;
  requiredAttendees?: string[];
  optionalAttendees?: string[];
}

// --- Appointment item surface (only when the pane is activated on a calendar
// item). In READ mode the fields are plain values (`start`/`end` are Dates,
// `location`/`subject` strings, attendees arrays); in COMPOSE (organizer) mode
// the same fields are async objects with get/setAsync. We detect the mode from
// `item.start.setAsync` (mirrors how messages key off `item.to.addAsync`).

/** Compose-mode appointment time field (`start` / `end`), Mailbox 1.1. */
interface ApptTime {
  getAsync(cb: AsyncCallback<Date>): void;
  setAsync(dateTime: Date, options: unknown, cb: AsyncCallback<void>): void;
}

/** Compose-mode appointment location field, Mailbox 1.1. */
interface ApptLocation {
  getAsync(cb: AsyncCallback<string>): void;
  setAsync(location: string, options: unknown, cb: AsyncCallback<void>): void;
}

interface MailboxItem {
  itemType?: string;
  // Read-mode (plain values) ↘  Compose-mode (async objects) ↙
  subject?: string | ComposeSubject;
  normalizedSubject?: string;
  from?: RecipientDetail;
  sender?: RecipientDetail;
  to?: RecipientDetail[] | ComposeRecipients;
  cc?: RecipientDetail[] | ComposeRecipients;
  bcc?: RecipientDetail[] | ComposeRecipients;
  body: MessageBody;
  dateTimeCreated?: Date;
  conversationId?: string;
  internetMessageId?: string;
  itemId?: string;
  attachments?: AttachmentDetail[];
  categories?: ItemCategories;
  getAttachmentsAsync?: (cb: AsyncCallback<AttachmentDetail[]>) => void;
  // Read-mode only (Mailbox 1.8 / 1.1).
  getAllInternetHeadersAsync?: (cb: AsyncCallback<string>) => void;
  displayReplyForm?: (formData: ReplyFormData | string) => void;
  displayReplyAllForm?: (formData: ReplyFormData | string) => void;
  // Compose-mode only (Mailbox 1.1 / 1.3).
  addFileAttachmentAsync?: (uri: string, name: string, cb: AsyncCallback<string>) => void;
  removeAttachmentAsync?: (attachmentId: string, cb: AsyncCallback<void>) => void;
  saveAsync?: (cb: AsyncCallback<string>) => void;
  // Appointment-only (present when itemType === "appointment"). Read mode: plain
  // values; compose (organizer) mode: async objects. `requiredAttendees` /
  // `optionalAttendees` reuse the ComposeRecipients shape (add/set/getAsync).
  start?: Date | ApptTime;
  end?: Date | ApptTime;
  location?: string | ApptLocation;
  organizer?: RecipientDetail;
  requiredAttendees?: RecipientDetail[] | ComposeRecipients;
  optionalAttendees?: RecipientDetail[] | ComposeRecipients;
  recurrence?: unknown;
}

interface MailboxNamespace {
  userProfile?: { displayName?: string; emailAddress?: string; timeZone?: string };
  item?: unknown;
  // Mailbox 1.6 — opens a brand-new message form, independent of the open item.
  displayNewMessageForm?: (parameters: NewMessageForm) => void;
  // Mailbox 1.1 — opens a brand-new appointment/meeting form (the user saves it).
  displayNewAppointmentForm?: (parameters: AppointmentForm) => void;
}

// ---------------------------------------------------------------------------
// Office async plumbing
// ---------------------------------------------------------------------------

/**
 * Promisify a single Office callback-style async call. `invoke` receives the
 * callback to hand to the Office method, e.g.
 *   await callOffice<string>((cb) => item.body.getAsync(Office.CoercionType.Text, cb));
 */
function callOffice<T>(invoke: (cb: AsyncCallback<T>) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    invoke((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message || "Office async operation failed."));
      }
    });
  });
}

function mailbox(): MailboxNamespace {
  const mb = Office.context && Office.context.mailbox;
  if (!mb) {
    throw new Error("The Outlook mailbox is not available in this context.");
  }
  return mb as unknown as MailboxNamespace;
}

function currentItem(): MailboxItem {
  const item = mailbox().item;
  if (!item) {
    throw new Error("No message is open. Open a message (or start composing one) and try again.");
  }
  return item as unknown as MailboxItem;
}

/** "appointment" when the pane is activated on a calendar item, else "message". */
function itemTypeOf(item: MailboxItem): "appointment" | "message" {
  return item.itemType === "appointment" ? "appointment" : "message";
}

/**
 * Whether the open item is being composed/edited (vs. read). The writable shape
 * differs by item type, so we detect from the field that gains a setter in
 * compose mode: messages expose `item.to.addAsync`; appointments (organizer
 * mode) expose `item.start.setAsync`. Read-mode values lack both.
 */
function isComposeMode(item: MailboxItem): boolean {
  if (itemTypeOf(item) === "appointment") {
    return typeof (item.start as ApptTime | undefined)?.setAsync === "function";
  }
  return typeof (item.to as ComposeRecipients | undefined)?.addAsync === "function";
}

/** Gate an appointment write tool to organizer/compose mode with a clear error. */
function requireApptCompose(item: MailboxItem, action: string): void {
  if (!isComposeMode(item)) {
    throw new Error(
      `"${action}" only works on an appointment you're editing (open from the organizer/compose window), ` +
        `but this pane is attached to an appointment open in read mode. A task pane stays bound to the item it ` +
        `was opened from, so it can't edit a separate appointment window. Open the Usable pane from the ` +
        `appointment you're organizing and try again.`
    );
  }
}

function requireCompose(item: MailboxItem, action: string): void {
  if (!isComposeMode(item)) {
    throw new Error(
      `"${action}" only works on a message you're composing, but this pane is attached to a message open in read mode. ` +
        `A task pane stays bound to the item it was opened from, so it can't edit a separate draft window. ` +
        `To edit a draft, open the Usable pane from the compose window (the Usable button shown while writing or replying); ` +
        `or use "reply"/"reply_all" to start a reply from here.`
    );
  }
}

function requireRead(item: MailboxItem, action: string): void {
  if (isComposeMode(item)) {
    throw new Error(
      `"${action}" is only available while reading a received message. A draft is currently open in compose mode.`
    );
  }
}

/**
 * Narrow the item's categories surface (Mailbox 1.8) for a given operation, or
 * throw a clear "not supported" error. Returns the field so call sites can drop
 * the non-null assertion. Centralises the guard shared by the three category tools.
 */
function requireCategories(
  item: MailboxItem,
  op: "getAsync" | "addAsync" | "removeAsync"
): ItemCategories {
  const cats = item.categories;
  if (!cats || typeof cats[op] !== "function") {
    throw new Error(
      "Categories aren't supported by this Outlook client (needs Mailbox requirement set 1.8)."
    );
  }
  return cats;
}

function coercion(format: unknown): Office.CoercionType {
  return String(format).toLowerCase() === "text"
    ? Office.CoercionType.Text
    : Office.CoercionType.Html;
}

function mapRecipient(r: RecipientDetail): { name: string; address: string } {
  return { name: r.displayName ?? "", address: r.emailAddress ?? "" };
}

/** Parse an ISO 8601 date-time string into a Date, or throw a clear error. */
function toDate(value: unknown, field: string): Date | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const d = new Date(value as string);
  if (isNaN(d.getTime())) {
    throw new Error(`"${field}" must be a valid ISO 8601 date-time (e.g. "2026-07-01T14:00:00"); got: ${String(value)}.`);
  }
  return d;
}

/** Compose-mode recipient line by name, or `undefined` if the field is absent. */
function recipientField(item: MailboxItem, line: "to" | "cc" | "bcc"): ComposeRecipients | undefined {
  return item[line] as ComposeRecipients | undefined;
}

/** True for a compose-mode field that exposes an async `getAsync`. */
function isAsyncField(field: unknown): field is { getAsync: (cb: AsyncCallback<unknown>) => void } {
  return typeof (field as { getAsync?: unknown } | undefined)?.getAsync === "function";
}

/** Read an appointment time field (Date in read mode, ApptTime in compose) as ISO. */
async function readApptDate(field: Date | ApptTime | undefined): Promise<string | null> {
  if (field === undefined || field === null) return null;
  const d = isAsyncField(field)
    ? await callOffice<Date>((cb) => (field as ApptTime).getAsync(cb))
    : (field as Date);
  return d ? new Date(d).toISOString() : null;
}

/** Read an appointment string field (subject/location: plain in read, async in compose). */
async function readApptString(
  field: string | ApptLocation | ComposeSubject | undefined
): Promise<string> {
  if (field === undefined || field === null) return "";
  if (isAsyncField(field)) {
    return (await callOffice<string>((cb) => (field as ApptLocation).getAsync(cb))) ?? "";
  }
  return field as string;
}

/** Read an attendee line (array in read mode, Recipients in compose) as mapped recipients. */
async function readAttendees(
  field: RecipientDetail[] | ComposeRecipients | undefined
): Promise<Array<{ name: string; address: string }>> {
  if (!field) return [];
  if (Array.isArray(field)) return field.map(mapRecipient);
  if (isAsyncField(field)) {
    const list = await callOffice<RecipientDetail[]>((cb) => (field as ComposeRecipients).getAsync(cb));
    return (list ?? []).map(mapRecipient);
  }
  return [];
}

/**
 * Whether the appointment recurs. The shape differs by mode and a naive
 * `!!item.recurrence` is wrong in compose: in READ mode `item.recurrence` is the
 * populated `Recurrence` object (or `null` for a single appointment), but in
 * COMPOSE mode it's an async *accessor* that is non-null even for a one-off — so
 * we must read the real value via `getAsync` there. Branch on the known mode so
 * we never depend on whether a read-mode recurrence object also exposes getAsync.
 */
async function readIsRecurring(item: MailboxItem): Promise<boolean> {
  if (!isComposeMode(item)) return !!item.recurrence;
  const rec = item.recurrence;
  if (!isAsyncField(rec)) return !!rec;
  return !!(await callOffice<unknown>((cb) => rec.getAsync(cb)));
}

/** The two appointment attendee lines, shared by the add/set attendee tools. */
const APPT_ATTENDEE_LINES = [
  { param: "required", key: "requiredAttendees" },
  { param: "optional", key: "optionalAttendees" },
] as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// `caps` is passed through from the shell's tool-call capabilities. The Office.js
// tools ignore it (they read the open item directly); the Graph tools use its
// `acquireGraphToken` to reach Microsoft Graph. Optional so unit tests can call a
// handler without a capabilities object.
export type OutlookToolHandler = (
  args: Record<string, unknown>,
  caps?: ToolCallCapabilities
) => Promise<unknown>;

/**
 * Which open-item type a tool applies to. The dispatcher routes on the open
 * item's `itemType` so a message tool fails with a clear error on a calendar
 * appointment (and vice versa) instead of mis-reading a foreign item shape.
 *   - "message"     — needs an email message open (default; most tools).
 *   - "appointment" — needs a calendar appointment open.
 *   - "any"         — item-type agnostic (info + new-form openers).
 */
export type ItemScope = "message" | "appointment" | "any";

export interface OutlookTool {
  schema: ParentToolSchema;
  handler: OutlookToolHandler;
  /** Defaults to "message" when omitted. */
  scope?: ItemScope;
}

// ---------------------------------------------------------------------------
// Tool definitions
// ---------------------------------------------------------------------------

const tools: OutlookTool[] = [
  // ─── Mailbox / item info ──────────────────────────────────────────────────

  {
    scope: "any",
    schema: {
      name: "get_mailbox_info",
      description:
        "Get information about the current mailbox and the open item: the signed-in user (display name, email, time zone), the item type ('message' or 'appointment'), and whether the item is open in 'read' or 'compose' mode. Call this first to decide which tools are available — message tools work on a message, appointment tools work on a calendar appointment.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      const item = currentItem();
      const profile = (mailbox().userProfile ?? {}) as {
        displayName?: string;
        emailAddress?: string;
        timeZone?: string;
      };
      return {
        user: {
          displayName: profile.displayName ?? "",
          emailAddress: profile.emailAddress ?? "",
          timeZone: profile.timeZone ?? "",
        },
        itemType: item.itemType ?? "message",
        mode: isComposeMode(item) ? "compose" : "read",
      };
    },
  },

  // ─── Read ─────────────────────────────────────────────────────────────────

  {
    schema: {
      name: "get_message_metadata",
      description:
        "Get the current message's header fields: subject, sender (read mode only), recipients (to/cc/bcc), created date, and conversation id. Works in both read and compose mode; in compose mode the subject and recipients are read from the in-progress draft.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      const item = currentItem();

      if (!isComposeMode(item)) {
        const to = (item.to as RecipientDetail[] | undefined) ?? [];
        const cc = (item.cc as RecipientDetail[] | undefined) ?? [];
        const from = item.from ?? item.sender;
        return {
          mode: "read",
          subject: (item.subject as string | undefined) ?? "",
          normalizedSubject: item.normalizedSubject ?? "",
          from: from ? mapRecipient(from) : null,
          to: to.map(mapRecipient),
          cc: cc.map(mapRecipient),
          dateTimeCreated: item.dateTimeCreated ? new Date(item.dateTimeCreated).toISOString() : null,
          conversationId: item.conversationId ?? null,
          internetMessageId: item.internetMessageId ?? null,
          hasAttachments: (item.attachments?.length ?? 0) > 0,
        };
      }

      // Compose mode — fields are async. `to` is guaranteed (mode detection keys
      // off `item.to.addAsync`); cc/bcc are guarded as possibly-absent surfaces so
      // a missing line returns [] instead of throwing a raw TypeError.
      const ccField = recipientField(item, "cc");
      const bccField = recipientField(item, "bcc");
      const noRecipients = Promise.resolve([] as RecipientDetail[]);
      const [subject, to, cc, bcc] = await Promise.all([
        callOffice<string>((cb) => (item.subject as ComposeSubject).getAsync(cb)),
        callOffice<RecipientDetail[]>((cb) => (item.to as ComposeRecipients).getAsync(cb)),
        ccField ? callOffice<RecipientDetail[]>((cb) => ccField.getAsync(cb)) : noRecipients,
        bccField ? callOffice<RecipientDetail[]>((cb) => bccField.getAsync(cb)) : noRecipients,
      ]);
      return {
        mode: "compose",
        subject: subject ?? "",
        to: (to ?? []).map(mapRecipient),
        cc: (cc ?? []).map(mapRecipient),
        bcc: (bcc ?? []).map(mapRecipient),
      };
    },
  },

  {
    schema: {
      name: "get_message_body",
      description:
        "Get the body of the current message. Use format 'text' (default) for plain text suitable for summarizing, or 'html' for the rich markup. Works in both read and compose mode.",
      parameters: {
        type: "object",
        properties: {
          format: {
            type: "string",
            enum: ["text", "html"],
            description: "Body coercion. Defaults to 'text'.",
          },
        },
      },
    },
    handler: async (args) => {
      const item = currentItem();
      const co = coercion(args.format ?? "text");
      const body = await callOffice<string>((cb) => item.body.getAsync(co, cb));
      return { format: co === Office.CoercionType.Text ? "text" : "html", body };
    },
  },

  {
    schema: {
      name: "get_attachments",
      description:
        "List metadata (name, content type, size, inline flag) for the attachments on the current message. Does not download attachment content.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      const item = currentItem();

      // Read mode exposes a plain array; compose mode (Mailbox 1.8+) exposes
      // getAttachmentsAsync. Fall back gracefully if neither is available.
      let list: AttachmentDetail[] = [];
      if (Array.isArray(item.attachments)) {
        list = item.attachments;
      } else if (typeof item.getAttachmentsAsync === "function") {
        list = (await callOffice<AttachmentDetail[]>((cb) => item.getAttachmentsAsync!(cb))) ?? [];
      }

      return {
        count: list.length,
        attachments: list.map((a) => ({
          id: a.id ?? null,
          name: a.name ?? "",
          contentType: a.contentType ?? a.attachmentType ?? "",
          size: a.size ?? null,
          isInline: a.isInline ?? false,
        })),
      };
    },
  },

  {
    schema: {
      name: "get_internet_headers",
      description:
        "Get the raw internet (MIME) headers of the received message as a single string — useful for inspecting routing (Received chain), authentication results (SPF/DKIM/DMARC), mailing-list headers, etc. Read mode only; requires a mailbox that supports Mailbox requirement set 1.8.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      const item = currentItem();
      requireRead(item, "get_internet_headers");
      if (typeof item.getAllInternetHeadersAsync !== "function") {
        throw new Error(
          "Reading internet headers isn't supported by this Outlook client (needs Mailbox requirement set 1.8)."
        );
      }
      const headers = await callOffice<string>((cb) => item.getAllInternetHeadersAsync!(cb));
      return { headers: headers ?? "" };
    },
  },

  {
    schema: {
      name: "get_categories",
      description:
        "List the categories (colour-coded labels) currently applied to the message. Works in both read and compose mode. Requires Mailbox requirement set 1.8.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      const item = currentItem();
      const categories = requireCategories(item, "getAsync");
      const cats = (await callOffice<CategoryDetail[]>((cb) => categories.getAsync(cb))) ?? [];
      return {
        count: cats.length,
        categories: cats.map((c) => ({ name: c.displayName ?? "", color: c.color ?? null })),
      };
    },
  },

  // ─── Reply (read mode) — the reply-safe entry points ───────────────────────
  // These open a reply *form* with our text placed above the quoted original
  // thread, which Outlook fills in for us. This is the safe way to answer a
  // message: the quote is never touched. Prefer these over set_body for replies.

  {
    schema: {
      name: "reply",
      description:
        "Open a reply to the sender of the message being read, pre-filled with the given body ABOVE the original quoted thread (the quote is preserved automatically). The user reviews and sends. Read mode only. This is the reply-safe way to answer a message — do NOT use set_body to reply.",
      parameters: {
        type: "object",
        properties: {
          body: {
            type: "string",
            description:
              "The reply body as an HTML fragment (e.g. \"<p>Thanks!</p>\"), placed above the quoted thread. Always send HTML — escape any literal < & characters; raw plain text is not formatted.",
          },
        },
        required: ["body"],
      },
    },
    handler: async (args) => {
      const item = currentItem();
      requireRead(item, "reply");
      if (typeof item.displayReplyForm !== "function") {
        throw new Error("Opening a reply form isn't supported by this Outlook client.");
      }
      item.displayReplyForm({ htmlBody: (args.body as string) ?? "" });
      return { success: true, opened: "reply" };
    },
  },

  {
    schema: {
      name: "reply_all",
      description:
        "Open a reply-all to everyone on the message being read, pre-filled with the given body ABOVE the original quoted thread (the quote is preserved automatically). The user reviews and sends. Read mode only. Reply-safe — do NOT use set_body to reply.",
      parameters: {
        type: "object",
        properties: {
          body: {
            type: "string",
            description:
              "The reply body as an HTML fragment (e.g. \"<p>Thanks!</p>\"), placed above the quoted thread. Always send HTML — escape any literal < & characters; raw plain text is not formatted.",
          },
        },
        required: ["body"],
      },
    },
    handler: async (args) => {
      const item = currentItem();
      requireRead(item, "reply_all");
      if (typeof item.displayReplyAllForm !== "function") {
        throw new Error("Opening a reply-all form isn't supported by this Outlook client.");
      }
      item.displayReplyAllForm({ htmlBody: (args.body as string) ?? "" });
      return { success: true, opened: "reply_all" };
    },
  },

  {
    scope: "any",
    schema: {
      name: "compose_new_message",
      description:
        "Open a brand-new message form (independent of the current item), pre-filled with optional recipients, subject and body. The user reviews and sends. Works in both read and compose mode. Requires Mailbox requirement set 1.6.",
      parameters: {
        type: "object",
        properties: {
          to: { type: "array", description: "Email addresses for the To line." },
          cc: { type: "array", description: "Email addresses for the Cc line." },
          bcc: { type: "array", description: "Email addresses for the Bcc line." },
          subject: { type: "string", description: "The subject line." },
          body: { type: "string", description: "The message body as HTML." },
        },
      },
    },
    handler: async (args) => {
      const mb = mailbox();
      if (typeof mb.displayNewMessageForm !== "function") {
        throw new Error(
          "Opening a new message form isn't supported by this Outlook client (needs Mailbox requirement set 1.6)."
        );
      }
      const form: NewMessageForm = {};
      if (Array.isArray(args.to)) form.toRecipients = args.to as string[];
      if (Array.isArray(args.cc)) form.ccRecipients = args.cc as string[];
      if (Array.isArray(args.bcc)) form.bccRecipients = args.bcc as string[];
      if (typeof args.subject === "string") form.subject = args.subject;
      if (typeof args.body === "string") form.htmlBody = args.body;
      mb.displayNewMessageForm(form);
      return { success: true, opened: "new_message" };
    },
  },

  {
    scope: "any",
    schema: {
      name: "create_appointment",
      description:
        "Open a prefilled NEW calendar appointment/meeting form — the user reviews and saves (or sends) it; nothing is written to the calendar silently. Provide a subject, start and end as ISO 8601 date-times, and optionally a location, body (HTML), and attendee email arrays (adding attendees turns it into a meeting invite). Works from both read and compose mail contexts. Requires Mailbox requirement set 1.1.",
      parameters: {
        type: "object",
        properties: {
          subject: { type: "string", description: "The appointment subject / title." },
          start: {
            type: "string",
            description: "Start date-time as ISO 8601, e.g. \"2026-07-01T14:00:00\".",
          },
          end: {
            type: "string",
            description: "End date-time as ISO 8601 (should be after start), e.g. \"2026-07-01T14:30:00\".",
          },
          location: { type: "string", description: "The location text." },
          body: { type: "string", description: "The appointment body / notes as HTML." },
          requiredAttendees: {
            type: "array",
            description: "Email addresses of required attendees (adding any makes this a meeting invite).",
          },
          optionalAttendees: {
            type: "array",
            description: "Email addresses of optional attendees.",
          },
        },
      },
    },
    handler: async (args) => {
      const mb = mailbox();
      if (typeof mb.displayNewAppointmentForm !== "function") {
        throw new Error(
          "Opening a new appointment form isn't supported by this Outlook client (needs Mailbox requirement set 1.1)."
        );
      }
      const form: AppointmentForm = {};
      if (typeof args.subject === "string") form.subject = args.subject;
      if (typeof args.location === "string") form.location = args.location;
      if (typeof args.body === "string") form.body = args.body;
      const start = toDate(args.start, "start");
      const end = toDate(args.end, "end");
      if (start && end && end.getTime() <= start.getTime()) {
        throw new Error('"end" must be after "start".');
      }
      if (start) form.start = start;
      if (end) form.end = end;
      if (Array.isArray(args.requiredAttendees)) form.requiredAttendees = args.requiredAttendees as string[];
      if (Array.isArray(args.optionalAttendees)) form.optionalAttendees = args.optionalAttendees as string[];
      mb.displayNewAppointmentForm(form);
      return { success: true, opened: "appointment" };
    },
  },

  // ─── Appointment (calendar item) ───────────────────────────────────────────
  // Active only when the pane is opened FROM a calendar appointment (the
  // manifest activates on appointment surfaces). Reading works on any open
  // appointment; the setters need organizer/compose mode (the appointment you
  // organize), same pane-binding rule as message compose. Stays at read/write
  // item — no Graph, no extra permission. Editing other calendar events or
  // deleting an appointment have no Office.js path (Graph only).

  {
    scope: "appointment",
    schema: {
      name: "get_appointment_details",
      description:
        "Get the details of the calendar appointment that's open: subject, start and end (ISO 8601), location, organizer, required and optional attendees, whether it recurs, applied categories, and the body/notes. Works whether the appointment is open in read or organizer (compose) mode. Use this when the open item is an appointment rather than an email.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      const item = currentItem();
      const [subject, start, end, location, required, optional, body, isRecurring] = await Promise.all([
        readApptString(item.subject),
        readApptDate(item.start),
        readApptDate(item.end),
        readApptString(item.location),
        readAttendees(item.requiredAttendees),
        readAttendees(item.optionalAttendees),
        callOffice<string>((cb) => item.body.getAsync(Office.CoercionType.Text, cb)),
        readIsRecurring(item),
      ]);
      // Categories are optional (Mailbox 1.8) — read softly, omit if unsupported.
      let categories: Array<{ name: string; color: string | null }> = [];
      if (isAsyncField(item.categories)) {
        const cats =
          (await callOffice<CategoryDetail[]>((cb) => item.categories!.getAsync(cb))) ?? [];
        categories = cats.map((c) => ({ name: c.displayName ?? "", color: c.color ?? null }));
      }
      return {
        mode: isComposeMode(item) ? "compose" : "read",
        itemType: "appointment",
        subject,
        start,
        end,
        location,
        organizer: item.organizer ? mapRecipient(item.organizer) : null,
        requiredAttendees: required,
        optionalAttendees: optional,
        isRecurring,
        categories,
        body,
      };
    },
  },

  {
    scope: "appointment",
    schema: {
      name: "set_appointment_time",
      description:
        "Set the start and/or end time of the appointment being organized. Provide start and/or end as ISO 8601 date-times (e.g. \"2026-07-01T15:00:00\"); an omitted field is left unchanged. If both are given, end must be after start. Organizer/compose mode only — fails with a clear error on an appointment open in read mode.",
      parameters: {
        type: "object",
        properties: {
          start: { type: "string", description: "New start date-time as ISO 8601." },
          end: { type: "string", description: "New end date-time as ISO 8601 (after start)." },
        },
      },
    },
    handler: async (args) => {
      const item = currentItem();
      requireApptCompose(item, "set_appointment_time");
      const start = toDate(args.start, "start");
      const end = toDate(args.end, "end");
      if (!start && !end) {
        throw new Error('Provide a new "start" and/or "end" time (ISO 8601).');
      }
      if (start && end && end.getTime() <= start.getTime()) {
        throw new Error('"end" must be after "start".');
      }
      if (start) {
        await callOffice<void>((cb) => (item.start as ApptTime).setAsync(start, {}, cb));
      }
      if (end) {
        await callOffice<void>((cb) => (item.end as ApptTime).setAsync(end, {}, cb));
      }
      return { success: true };
    },
  },

  {
    scope: "appointment",
    schema: {
      name: "set_appointment_location",
      description:
        "Set the location text of the appointment being organized. Organizer/compose mode only.",
      parameters: {
        type: "object",
        properties: {
          location: { type: "string", description: "The new location text." },
        },
        required: ["location"],
      },
    },
    handler: async (args) => {
      const item = currentItem();
      requireApptCompose(item, "set_appointment_location");
      await callOffice<void>((cb) =>
        (item.location as ApptLocation).setAsync(args.location as string, {}, cb)
      );
      return { success: true };
    },
  },

  {
    scope: "appointment",
    schema: {
      name: "add_appointment_attendees",
      description:
        "Add attendees to the appointment being organized without removing existing ones. Provide arrays of email addresses for `required` and/or `optional`. Organizer/compose mode only.",
      parameters: {
        type: "object",
        properties: {
          required: { type: "array", description: "Email addresses to add as required attendees." },
          optional: { type: "array", description: "Email addresses to add as optional attendees." },
        },
      },
    },
    handler: async (args) => {
      const item = currentItem();
      requireApptCompose(item, "add_appointment_attendees");
      const added: Record<string, number> = {};
      for (const line of APPT_ATTENDEE_LINES) {
        const addrs = (args[line.param] as string[] | undefined) ?? [];
        if (!addrs.length) continue;
        const field = item[line.key] as ComposeRecipients | undefined;
        if (!field || typeof field.addAsync !== "function") continue;
        await callOffice<void>((cb) => field.addAsync(addrs, {}, cb));
        added[line.param] = addrs.length;
      }
      return { success: true, added };
    },
  },

  {
    scope: "appointment",
    schema: {
      name: "set_appointment_attendees",
      description:
        "Replace the attendees of the appointment being organized. Provide arrays of email addresses for `required` and/or `optional`; an omitted field is left unchanged, an empty array clears that line. Organizer/compose mode only.",
      parameters: {
        type: "object",
        properties: {
          required: { type: "array", description: "Email addresses for required attendees (replaces existing)." },
          optional: { type: "array", description: "Email addresses for optional attendees (replaces existing)." },
        },
      },
    },
    handler: async (args) => {
      const item = currentItem();
      requireApptCompose(item, "set_appointment_attendees");
      for (const line of APPT_ATTENDEE_LINES) {
        if (args[line.param] === undefined) continue;
        const field = item[line.key] as ComposeRecipients | undefined;
        if (!field || typeof field.setAsync !== "function") continue;
        await callOffice<void>((cb) => field.setAsync(args[line.param] as string[], {}, cb));
      }
      return { success: true };
    },
  },

  // ─── Write (compose mode only) ─────────────────────────────────────────────

  {
    schema: {
      name: "set_subject",
      description:
        "Set the subject of the message being composed. Compose mode only — fails with a clear error in read mode.",
      parameters: {
        type: "object",
        properties: {
          subject: { type: "string", description: "The new subject line." },
        },
        required: ["subject"],
      },
    },
    handler: async (args) => {
      const item = currentItem();
      requireCompose(item, "set_subject");
      await callOffice<void>((cb) =>
        (item.subject as ComposeSubject).setAsync(args.subject as string, {}, cb)
      );
      return { success: true };
    },
  },

  {
    schema: {
      name: "set_body",
      description:
        "Replace the ENTIRE body of the message being composed. Use this only when writing a fresh message from scratch — it overwrites everything, so it will WIPE the quoted thread of a reply or forward. To answer a reply/forward draft, use prepend_to_body or insert_at_cursor instead (and use the reply / reply_all tools to start a reply from a read message). Use format 'html' (default) or 'text'. Compose mode only.",
      parameters: {
        type: "object",
        properties: {
          content: { type: "string", description: "The new body content (replaces everything)." },
          format: {
            type: "string",
            enum: ["html", "text"],
            description: "Coercion of `content`. Defaults to 'html'.",
          },
        },
        required: ["content"],
      },
    },
    handler: async (args) => {
      const item = currentItem();
      requireCompose(item, "set_body");
      await callOffice<void>((cb) =>
        item.body.setAsync(args.content as string, { coercionType: coercion(args.format ?? "html") }, cb)
      );
      return { success: true };
    },
  },

  {
    schema: {
      name: "prepend_to_body",
      description:
        "Add content to the BEGINNING of the body of the message being composed, leaving the rest of the draft intact. This is the reply-safe way to add your text to an open reply/forward draft: it places your text above the quoted thread without overwriting it. Use format 'html' (default) or 'text'. Compose mode only.",
      parameters: {
        type: "object",
        properties: {
          content: { type: "string", description: "Content to insert at the top of the body." },
          format: {
            type: "string",
            enum: ["html", "text"],
            description: "Coercion of `content`. Defaults to 'html'.",
          },
        },
        required: ["content"],
      },
    },
    handler: async (args) => {
      const item = currentItem();
      requireCompose(item, "prepend_to_body");
      await callOffice<void>((cb) =>
        item.body.prependAsync(
          args.content as string,
          { coercionType: coercion(args.format ?? "html") },
          cb
        )
      );
      return { success: true };
    },
  },

  {
    schema: {
      name: "insert_at_cursor",
      description:
        "Insert content into the message body at the current cursor position (or replace the current selection) without overwriting the rest of the draft. Reply-safe when the cursor is above the quote. Compose mode only.",
      parameters: {
        type: "object",
        properties: {
          content: { type: "string", description: "Content to insert." },
          format: {
            type: "string",
            enum: ["html", "text"],
            description: "Coercion of `content`. Defaults to 'html'.",
          },
        },
        required: ["content"],
      },
    },
    handler: async (args) => {
      const item = currentItem();
      requireCompose(item, "insert_at_cursor");
      await callOffice<void>((cb) =>
        item.body.setSelectedDataAsync(
          args.content as string,
          { coercionType: coercion(args.format ?? "html") },
          cb
        )
      );
      return { success: true };
    },
  },

  {
    schema: {
      name: "add_recipients",
      description:
        "Add recipients to the message being composed without removing existing ones. Provide arrays of email addresses for `to`, `cc` and/or `bcc`. Compose mode only.",
      parameters: {
        type: "object",
        properties: {
          to: { type: "array", description: "Email addresses to add to the To line." },
          cc: { type: "array", description: "Email addresses to add to the Cc line." },
          bcc: { type: "array", description: "Email addresses to add to the Bcc line." },
        },
      },
    },
    handler: async (args) => {
      const item = currentItem();
      requireCompose(item, "add_recipients");
      const added: Record<string, number> = {};
      for (const line of ["to", "cc", "bcc"] as const) {
        const addrs = (args[line] as string[] | undefined) ?? [];
        if (!addrs.length) continue;
        const field = recipientField(item, line);
        if (!field) continue;
        await callOffice<void>((cb) => field.addAsync(addrs, {}, cb));
        added[line] = addrs.length;
      }
      return { success: true, added };
    },
  },

  {
    schema: {
      name: "set_recipients",
      description:
        "Replace the recipients of the message being composed. Provide arrays of email addresses for `to`, `cc` and/or `bcc`; an omitted field is left unchanged, an empty array clears that line. Compose mode only.",
      parameters: {
        type: "object",
        properties: {
          to: { type: "array", description: "Email addresses for the To line (replaces existing)." },
          cc: { type: "array", description: "Email addresses for the Cc line (replaces existing)." },
          bcc: { type: "array", description: "Email addresses for the Bcc line (replaces existing)." },
        },
      },
    },
    handler: async (args) => {
      const item = currentItem();
      requireCompose(item, "set_recipients");
      for (const line of ["to", "cc", "bcc"] as const) {
        if (args[line] === undefined) continue;
        const field = recipientField(item, line);
        if (!field) continue;
        await callOffice<void>((cb) => field.setAsync(args[line] as string[], {}, cb));
      }
      return { success: true };
    },
  },

  {
    schema: {
      name: "add_categories",
      description:
        "Apply one or more existing mailbox categories (colour-coded labels) to the message. Provide an array of category display names that ALREADY EXIST in the mailbox — applying a name that doesn't exist fails. This add-in can't enumerate the mailbox's category list (that needs a higher mailbox-wide permission it intentionally doesn't request), so use a name the user gives you or a well-known Outlook default such as \"Red Category\" or \"Blue Category\". Requires Mailbox requirement set 1.8.",
      parameters: {
        type: "object",
        properties: {
          categories: { type: "array", description: "Category display names to apply." },
        },
        required: ["categories"],
      },
    },
    handler: async (args) => {
      const item = currentItem();
      const categories = requireCategories(item, "addAsync");
      const names = (args.categories as string[] | undefined) ?? [];
      if (names.length) {
        await callOffice<void>((cb) => categories.addAsync(names, cb));
      }
      return { success: true, added: names.length };
    },
  },

  {
    schema: {
      name: "remove_categories",
      description:
        "Remove one or more categories (colour-coded labels) from the message. Provide an array of category display names currently applied. Requires Mailbox requirement set 1.8.",
      parameters: {
        type: "object",
        properties: {
          categories: { type: "array", description: "Category display names to remove." },
        },
        required: ["categories"],
      },
    },
    handler: async (args) => {
      const item = currentItem();
      const categories = requireCategories(item, "removeAsync");
      const names = (args.categories as string[] | undefined) ?? [];
      if (names.length) {
        await callOffice<void>((cb) => categories.removeAsync(names, cb));
      }
      return { success: true, removed: names.length };
    },
  },

  {
    schema: {
      name: "add_file_attachment",
      description:
        "Attach a file to the message being composed by URL. The file at `uri` is fetched by Outlook and attached under `name`. Compose mode only. Returns the new attachment id.",
      parameters: {
        type: "object",
        properties: {
          uri: { type: "string", description: "A publicly reachable https URL to the file to attach." },
          name: { type: "string", description: "The file name to show on the attachment." },
        },
        required: ["uri", "name"],
      },
    },
    handler: async (args) => {
      const item = currentItem();
      requireCompose(item, "add_file_attachment");
      if (typeof item.addFileAttachmentAsync !== "function") {
        throw new Error("Adding attachments isn't supported by this Outlook client.");
      }
      const id = await callOffice<string>((cb) =>
        item.addFileAttachmentAsync!(args.uri as string, args.name as string, cb)
      );
      return { success: true, attachmentId: id ?? null };
    },
  },

  {
    schema: {
      name: "remove_attachment",
      description:
        "Remove an attachment from the message being composed by its attachment id (use get_attachments to find ids). Compose mode only.",
      parameters: {
        type: "object",
        properties: {
          attachmentId: { type: "string", description: "The id of the attachment to remove." },
        },
        required: ["attachmentId"],
      },
    },
    handler: async (args) => {
      const item = currentItem();
      requireCompose(item, "remove_attachment");
      if (typeof item.removeAttachmentAsync !== "function") {
        throw new Error("Removing attachments isn't supported by this Outlook client.");
      }
      await callOffice<void>((cb) => item.removeAttachmentAsync!(args.attachmentId as string, cb));
      return { success: true };
    },
  },

  {
    schema: {
      name: "save_draft",
      description:
        "Save the message being composed as a draft (it is NOT sent — the assistant never sends; the user does). Returns the saved item id. Compose mode only.",
      parameters: { type: "object", properties: {} },
    },
    handler: async () => {
      const item = currentItem();
      requireCompose(item, "save_draft");
      if (typeof item.saveAsync !== "function") {
        throw new Error("Saving a draft isn't supported by this Outlook client.");
      }
      const itemId = await callOffice<string>((cb) => item.saveAsync!(cb));
      return { success: true, itemId: itemId ?? null };
    },
  },
];

// Mailbox/calendar-wide Microsoft Graph tools (list_messages, search_messages,
// list_calendar_events). Scope "any": they don't read the open item, so the
// item-type router must not gate them. Appended here so they register in
// outlookToolSchemas and route through the same dedup/dispatch path as the Office
// tools — the only difference is their handler uses caps.acquireGraphToken.
for (const gt of graphTools) {
  tools.push({ schema: gt.schema, handler: gt.handler, scope: "any" });
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export const outlookToolSchemas: ParentToolSchema[] = tools.map((t) => t.schema);

const toolMap: Map<string, OutlookTool> = new Map(tools.map((t) => [t.schema.name, t]));

/**
 * Route a tool against the open item's type. A message tool fails clearly on a
 * calendar appointment (and vice versa) instead of mis-reading a foreign item
 * shape — an appointment has `start`/`requiredAttendees`, not `to`/`from`. We
 * read `itemType` directly (not via the throwing `currentItem`) so that, when no
 * item is open, the handler can raise its own "No message is open" error. Tools
 * scoped "any" (info + new-form openers) are never gated.
 */
function enforceItemScope(toolName: string, scope: ItemScope): void {
  if (scope === "any") return;
  const mb = Office.context && Office.context.mailbox;
  const open = (mb && mb.item ? mb.item : undefined) as MailboxItem | undefined;
  if (!open) return;
  const actual = itemTypeOf(open);
  if (actual !== scope) {
    const want = scope === "appointment" ? "a calendar appointment" : "an email message";
    const have = actual === "appointment" ? "a calendar appointment" : "an email message";
    throw new Error(
      `"${toolName}" works on ${want}, but ${have} is currently open. ` +
        (scope === "appointment"
          ? "Open the Usable pane from a calendar appointment to use it."
          : "Use the appointment tools (e.g. get_appointment_details) for a calendar item.")
    );
  }
}

// Deduplicate in-flight calls to guard against the Office.js / WKWebView
// double-delivery of a single TOOL_CALL (same pattern as excel-tools.ts — see
// the detailed comment there). Keyed on tool name + serialised args.
const inFlightCalls = new Map<string, Promise<unknown>>();

// The form-opening tools (reply / reply_all / compose_new_message) are the one
// case the in-flight guard above does NOT cover: they open a draft window via a
// *synchronous* Office call and resolve in the same tick, so two deliveries of
// the same TOOL_CALL don't overlap in-flight — the first settles and clears its
// key before the duplicate arrives, and a SECOND window opens (observed live:
// two identical reply drafts). Unlike the idempotent reads and the async writes
// (which overlap and so are already deduped), opening a window twice is a
// visible, non-idempotent side effect. So for just these tools we remember the
// result for a short cooldown and replay it for a duplicate that lands within it.
const FORM_OPENER_TOOLS = new Set([
  "reply",
  "reply_all",
  "compose_new_message",
  "create_appointment",
]);
const FORM_OPENER_COOLDOWN_MS = 1500;
const recentFormOpens = new Map<string, { at: number; result: unknown }>();

/** Test-only hook: reset the de-dup state between cases. */
export function __resetOutlookDedupForTests(): void {
  inFlightCalls.clear();
  recentFormOpens.clear();
}

export async function handleOutlookToolCall(
  toolName: string,
  args: unknown,
  caps?: ToolCallCapabilities
): Promise<unknown> {
  const tool = toolMap.get(toolName);
  if (!tool) {
    throw new Error(`Unknown Outlook tool: "${toolName}"`);
  }
  const handler = tool.handler;

  enforceItemScope(toolName, tool.scope ?? "message");

  const key = `${toolName}:${JSON.stringify(args)}`;
  const existing = inFlightCalls.get(key);
  if (existing) return existing;

  if (FORM_OPENER_TOOLS.has(toolName)) {
    const recent = recentFormOpens.get(key);
    if (recent && Date.now() - recent.at < FORM_OPENER_COOLDOWN_MS) {
      return recent.result;
    }
  }

  const promise = handler(args as Record<string, unknown>, caps).then(
    (result) => {
      inFlightCalls.delete(key);
      if (FORM_OPENER_TOOLS.has(toolName)) {
        recentFormOpens.set(key, { at: Date.now(), result });
        // Sweep expired entries on write so the map stays bounded to the cooldown
        // window. (`inFlightCalls` self-cleans on settle; this sibling won't unless
        // we prune it, or a long-lived pane slowly leaks stale results.)
        recentFormOpens.forEach((v, k) => {
          if (Date.now() - v.at >= FORM_OPENER_COOLDOWN_MS) recentFormOpens.delete(k);
        });
      }
      return result;
    },
    (err) => {
      inFlightCalls.delete(key);
      throw err;
    }
  );
  inFlightCalls.set(key, promise);
  return promise;
}
